/* @flow */
import React from 'react';
import { Motion, spring } from 'react-motion';
import ms from 'ms';
import { connect } from 'react-redux';
import type { Record, List } from 'immutable';
import classnames from 'classnames/bind';

import s from './FloatingAlertList.styl';
const cx = classnames.bind(s);
import * as Alerts from 'modules/alerts';
import {Button, IconButtonPure} from 'trustar-2.0/components/Button';
import {Icon, IconLookup} from 'trustar-2.0/components';

type Alert = Record<{
  id: string,
  type: 'success' | 'info' | 'warning' | 'error',
  text: string,
  htmlMessage?: React.Element<*>,
}>;

const ALERT_TIMEOUT = ms('3s');

type FloatingAlertProps = {
  alert: Alert,
  onClose: (alert: Alert) => void,
  timeoutInSeconds?: string, // This needs to be in the format of 3s, 5s, 10s etc.
};
type FloatingAlertState = {
  timeout: ?number,
  visible: boolean,
};

export class FloatingAlert extends React.Component {
  props: FloatingAlertProps;

  state: FloatingAlertState = {
    timeout: null,
    visible: false,
  };

  componentDidMount() {
    const {timeoutInSeconds} = this.props;
    const alertTimeout = timeoutInSeconds ? ms(timeoutInSeconds) : ALERT_TIMEOUT;

    this.clearTimeout();
    this.setState({ // eslint-disable-line react/no-did-mount-set-state
      timeout: setTimeout(this.closeAlert, alertTimeout),
      visible: true,
    });
  }

  componentWillUnmount() {
    this.clearTimeout();
  }

  // If a timeout is currently set for this alert then clear it.
  clearTimeout = () => {
    if (this.state.timeout) clearTimeout(this.state.timeout);
  };

  handleMouseLeave = () => {
    const {timeoutInSeconds} = this.props;
    const alertTimeout = timeoutInSeconds ? ms(timeoutInSeconds) : ALERT_TIMEOUT;
    this.setState({
      timeout: setTimeout(this.closeAlert, alertTimeout),
    });
  };

  closeAlert = () => {
    this.clearTimeout();
    this.props.onClose(this.props.alert);
  };

  getEndValue() {
    return {
      opacity: spring(this.state.visible ? 1 : 0),
      right: spring(this.state.visible ? 0 : -100),
    };
  }

  /**
   * Get the icon that pertains to the given type of alerts. Defaults to
   * no icon.
   */
  getIcon() {
    const type = this.props.alert.get('type');
    switch (type) {
    case 'success':
      return <i className='fa fa-check-circle'></i>;
    case 'info':
      return <i className='fa fa-info-circle'></i>;
    case 'warning':
      return <i className='fa fa-exclamation-triangle'></i>;
    case 'error':
      return <i className='fa fa-exclamation-circle'></i>;
    default:
      return null;
    }
  }

  render() {
    const { alert } = this.props;
    return (
      <li
        data-cy={'floating-alert'}
        onClick={this.closeAlert}
        onMouseEnter={this.clearTimeout}
        onMouseLeave={this.handleMouseLeave}>
        <Motion
          defaultStyle={{
            opacity: 0,
            right: -100,
          }}
          style={this.getEndValue()}>
          {values => (
            <div
              data-cy={`${alert.get('type')}-alert`}
              style={{
                opacity: values.opacity,
                right: values.right,
              }}
              className={cx(
                'alert',
                alert.get('type'),
                { visible: this.state.visible }
              )}>
              <div className={s.icon}>
                {this.getIcon()}
              </div>
              <div className={s.text}>
                {alert.get('htmlMessage') || alert.get('text')}
              </div>
              <div className={s.button}>
                <IconButtonPure transparent fill={'none'} icon={IconLookup.CROSS} />
              </div>
            </div>
          )}
        </Motion>
      </li>
    );
  }
}

/**
 * A list of alerts. This can include errors, warnings, etc. Much like
 * the console API.
 */
type FloatingAlertListProps = {
  alerts: List<Alert>,
  deleteAlert: Function,
};
export class FloatingAlertList extends React.Component {
  props: FloatingAlertListProps;

  handleCloseAlert = (alert: Alert): void => {
    this.props.deleteAlert(alert);
  };

  render() {
    return (
      <ul className={cx('FloatingAlertList')}>
        {this.props.alerts.map(alert => {
          return (
            <FloatingAlert
              key={alert.get('id')}
              onClose={this.handleCloseAlert}
              alert={alert} />
          );
        })}
      </ul>
    );
  }
}

const mapStateToProps = state => ({
  alerts: Alerts.getters.alertList(state),
});

const dispatchProps = {
  deleteAlert: Alerts.actions.deleteAlert
};

export default connect(mapStateToProps, dispatchProps)(FloatingAlertList);



// WEBPACK FOOTER //
// ./src/app/components/alerts/FloatingAlertList.js